import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "./api";

// Dezenter Zwei-Ton-Ping (880 → 660 Hz) bei neuer Kundennachricht (Team).
function playPing() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const t0 = ctx.currentTime;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
    osc.frequency.setValueAtTime(880, t0);
    osc.frequency.setValueAtTime(660, t0 + 0.18);
    osc.start(t0);
    osc.stop(t0 + 0.4);
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 600);
  } catch { /* ignore — browser sound might be muted / blocked */ }
}

/**
 * Feb 2026 · Lightweight global event bus for message-badge refresh.
 * Any consumer of `useUnreadMessages` re-fetches the count when this event
 * fires. Call `bumpUnreadMessages()` right after a mark-read side effect
 * (e.g. opening a chat) so the badge drops to 0 within the same tick — no
 * more waiting for the next 12 s poll.
 */
const UNREAD_REFRESH_EVENT = "weissblom:unread-refresh";
export function bumpUnreadMessages() {
  try {
    window.dispatchEvent(new CustomEvent(UNREAD_REFRESH_EVENT));
  } catch { /* browsers without CustomEvent — ignore */ }
}

/**
 * useUnreadMessages — polls the unread-message count for the current user
 * every ~12 s. Works for both roles:
 *   • Customer/family: `GET /api/me/notifications`  → returns `messages`
 *   • Admin/concierge: `GET /api/threads`           → sum of per-thread `unread`
 *
 * Also re-fetches immediately when the tab becomes visible again OR when
 * any component dispatches `bumpUnreadMessages()` (see above). Feb 2026 ·
 * single source of truth for every red message-flag badge in the Weissblom UI.
 *
 * @param {"customer"|"team"} role — used to pick the right endpoint.
 * @returns {{ total: number, byThread: Record<string, number>, refresh: () => void }}
 */
export function useUnreadMessages(role, scopeConciergeId = null) {
  const [total, setTotal] = useState(0);
  const [byThread, setByThread] = useState({});
  const alive = useRef(true);
  // Track previous count so we can detect a positive jump (new incoming
  // message during the active session) and fire a discreet ping + toast.
  // Team-only feature — customers already see the badge instantly on their
  // narrow layout, and a ping there would be noise for casual browsing.
  const prevTotal = useRef(0);
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    try {
      if (role === "team") {
        const url = scopeConciergeId
          ? `/threads?scope_concierge_id=${encodeURIComponent(scopeConciergeId)}`
          : "/threads";
        const { data } = await api.get(url);
        if (!alive.current) return;
        const map = {};
        let sum = 0;
        for (const t of data || []) {
          const n = Number(t.unread || 0);
          if (n > 0) map[t.thread_id] = n;
          sum += n;
        }
        // Feb 2026 · Ping + Toast bei neuer Nachricht (nur Team/Concierge).
        // Springt der Zähler VON KLEINER AUF GRÖSSER, feuert der Alert —
        // der erste Poll nach Login ist ausgenommen (sonst würde jedes Öffnen
        // der App klingen).
        if (!firstLoad.current && sum > prevTotal.current) {
          playPing();
          const delta = sum - prevTotal.current;
          toast.info(
            delta === 1
              ? "Neue Nachricht von einem Kunden eingegangen."
              : `${delta} neue Nachrichten von Kunden eingegangen.`,
            { duration: 5500, action: { label: "Anzeigen", onClick: () => { window.location.href = "/team/chat"; } } },
          );
        }
        firstLoad.current = false;
        prevTotal.current = sum;
        setByThread(map);
        setTotal(sum);
      } else if (role === "customer") {
        const { data } = await api.get("/me/notifications");
        if (!alive.current) return;
        const n = Number(data?.messages || 0);
        setTotal(n);
        // A customer only has their own thread — nothing to break down.
        setByThread({});
      }
    } catch { /* silent — badge just won't update this cycle */ }
  }, [role, scopeConciergeId]);

  useEffect(() => {
    alive.current = true;
    if (!role) return () => { alive.current = false; };
    load();
    const iv = setInterval(load, 12000);
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    const onBump = () => load();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(UNREAD_REFRESH_EVENT, onBump);
    return () => {
      alive.current = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(UNREAD_REFRESH_EVENT, onBump);
    };
  }, [role, load]);

  return { total, byThread, refresh: load };
}
